<template>
  <WdsModal
    ref="modal"
    :show="show"
    :title="$t('Get invitation link')"
    :z-index="4000"
    custom-size="610px"
    data-test="invite-link-modal"
    @cancel="handleCancel"
  >
    <template #default>
      <WdsAlert v-if="generateInviteLinkError" error class="tw-mb-2">
        {{ $t('An error occurred') }}
      </WdsAlert>
      <div v-if="!loading">
        <!-- Link has been generated-->
        <WdsStack v-if="view === null && contactInviteUrl" direction-column gap-md>
          <WdsText primary>
            {{ $t('Share this link with any freelancer, that you want to work with in Worksome.') }}
          </WdsText>

          <div class="tw-line-clamp-1 tw-break-all">
            <WdsButtonGhost
              class="tw-group tw-flex tw-items-center tw-gap-sm tw-w-full tw-max-w-fit"
              :class="{ 'copied-text': copiedInviteUrl }"
              data-test="invite-url"
              @click="copyInviteUrl(contactInviteUrl)"
            >
              <span class="tw-line-clamp-1 tw-text-left">
                {{ contactInviteUrl }}
              </span>

              <div v-if="!copiedInviteUrl" class="tw-invisible group-hover:tw-visible">
                <WdsIcon :name="WdsIconNameEnum.COPY" :size="16" />
              </div>
              <WdsText v-if="copiedInviteUrl" tertiary class="tw-break-normal">
                {{ $t('Copied!') }}
              </WdsText>
            </WdsButtonGhost>
          </div>

          <WdsText secondary>
            {{
              $t(
                'Once your invited freelancers have signed up through the generated link, you can approve them as your Trusted Contacts in Worksome.',
              )
            }}
          </WdsText>

          <div>
            <WdsButtonGhost data-test="new-link-button" secondary @click="toggleView">
              {{ $t('Generate new link') }}
            </WdsButtonGhost>
          </div>
        </WdsStack>

        <!-- Link needs to be generated-->
        <WdsStack v-else direction-column gap-md>
          <WdsHeading type="title2">{{ $t('Generate new invitation link') }}</WdsHeading>

          <WdsText secondary>
            {{
              $t(
                'Once your invited freelancers have signed up through the generated link, you can approve them as your Trusted Contacts in Worksome.',
              )
            }}
          </WdsText>

          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="contactInviteUrl"
            class="tw-text-red-700"
            v-html="
              $t(
                'When you click the button, <em>the existing link will be deactivated</em> and will no longer work for any freelancer, that you\'ve shared it with.',
              )
            "
          />
          <!-- eslint-enable vue/no-v-html -->

          <div>
            <WdsButton
              v-if="contactInviteUrl"
              data-test="generate-new-link-button"
              primary
              lg
              @click="generateNewLink"
            >
              <WdsStack align-center gap-sm>
                <WdsIcon name="link" size="20" />
                {{ $t('Generate new link') }}
              </WdsStack>
            </WdsButton>

            <WdsButton v-else data-test="generate-link-button" primary lg @click="generateLink">
              <WdsStack align-center gap-sm>
                <WdsIcon name="link" size="20" />
                {{ $t('Generate link') }}
              </WdsStack>
            </WdsButton>
          </div>

          <div v-if="contactInviteUrl">
            <WdsButtonGhost secondary @click="toggleView">
              {{ $t('Cancel') }}
            </WdsButtonGhost>
          </div>
        </WdsStack>
      </div>
    </template>
    <template #footer>
      <div class="tw-flex tw-justify-end">
        <WdsButton tertiary @click="handleCancel">
          {{ $t('Close') }}
        </WdsButton>
      </div>
    </template>
  </WdsModal>
</template>

<script setup>
import gql from 'graphql-tag'
import { useClipboard } from '@vueuse/core'
import { defineEmits, defineProps, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsModal,
  WdsStack,
  WdsButton,
  WdsButtonGhost,
  WdsText,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
} from '@worksome/wds-components'
import { $t } from '@/composables/translation'

defineProps({
  show: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

const { copy } = useClipboard()

const view = ref(null)
const contactInviteUrl = ref(null)
const copiedInviteUrl = ref(false)

const { onResult, loading } = useQuery(
  gql`
    query ContactInviteUrlQuery($id: ID!) {
      company(id: $id) {
        contactInviteUrl
      }
    }
  `,
  {
    id: currentAccount.globalId,
  },
  {
    clientId: 'publicApi',
  },
)

onResult((result) => {
  if (result.loading === false) {
    contactInviteUrl.value = result.data.company.contactInviteUrl ?? null
  }
})

const { mutate: generateInviteLink, error: generateInviteLinkError } = useMutation(
  gql`
    mutation GenerateInviteLinkMutation($company: ID!) {
      generateInviteLink(input: { company: $company }) {
        ... on Company {
          contactInviteUrl
        }
      }
    }
  `,
  {
    clientId: 'publicApi',
  },
)

const toggleView = () => {
  // TODO: This is very hacky :D
  if (contactInviteUrl.value && view.value === null) {
    view.value = 'something'
  } else {
    view.value = null
  }
}

const generateNewLink = () => {
  swal(
    {
      title: $t('Are you sure?'),
      text: $t('Do you want to replace your existing invitation link with a new link?'),
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: $t('Yes, generate new link'),
      closeOnConfirm: false,
    },
    async () => {
      swal($t('Processing'), $t('Generating a new link'), 'info')

      const response = await generateInviteLink({
        company: currentAccount.globalId,
      })

      contactInviteUrl.value = response.data?.generateInviteLink?.contactInviteUrl ?? null
      view.value = null

      swal($t('Success'), $t('The new link was successfully generated'), 'success')
    },
  )

  const confirmButton = document.querySelector('.sa-confirm-button-container button.confirm')

  if (confirmButton) {
    confirmButton.setAttribute('data-test', 'sa-confirm-button')
  }
}

const generateLink = async () => {
  swal($t('Processing'), $t('Generating the link'), 'info')

  const response = await generateInviteLink({
    company: currentAccount.globalId,
  })

  contactInviteUrl.value = response.data?.generateInviteLink?.contactInviteUrl ?? null
  view.value = null

  swal($t('Success'), $t('The link was successfully generated'), 'success')
}

const handleCancel = () => {
  generateInviteLinkError.value = null
  emit('close')
}

const copyInviteUrl = (text) => {
  copy(text)

  copiedInviteUrl.value = true

  setTimeout(() => {
    copiedInviteUrl.value = false
  }, 1500)
}
</script>

<style scoped lang="scss">
:deep(.wds-button-ghost.copied-text) {
  &:hover {
    background-color: transparent !important;
    @apply tw-ease-in;
    @apply tw-duration-300;
  }
}
</style>
